import { toast } from "sonner";
import { Award, Info, FileBadge } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useStore } from "@/store/AppStore";
import { PageHeader, Panel, ProgressLine } from "@/components/kit";
import { SealBadge } from "@/components/Seal";
import { companyRanking, SEALS } from "@/lib/quality";
import { MONTH_KEY, MONTH_LABEL } from "@/data/mock";
import { fmtDate } from "@/lib/format";

const PARTS = [["sales", "Vendas x meta", 35], ["tasks", "Tarefas", 25], ["checklists", "Checklists", 20], ["maintenance", "Manutenção", 20]];

function CompanyScoreCard({ r, referrals }) {
  const { setSeal } = useStore();
  const pending = r.suggested && r.seal?.level !== r.suggested;
  return (
    <li data-testid={`company-rank-${r.id}`} className="grid grid-cols-1 gap-5 py-6 lg:grid-cols-[auto_1.2fr_2fr_1.3fr] lg:items-center">
      <span className={`flex h-10 w-10 items-center justify-center rounded-full font-display text-sm font-bold ${r.position <= 3 ? "bg-gold text-neutral-900" : "bg-neutral-100 text-neutral-500"}`}>{r.position}º</span>
      <div>
        <p className="text-sm font-semibold">{r.name}</p>
        <p className="text-xs text-neutral-500">{r.segment} · {referrals} indicação(ões) recebida(s)</p>
        <p className="mt-2 font-display text-3xl font-semibold" data-testid={`company-score-${r.id}`}>{r.score}<span className="text-sm text-neutral-400"> pts</span></p>
      </div>
      <div className="grid grid-cols-2 gap-x-5 gap-y-3">
        {PARTS.map(([k, l, w]) => (
          <div key={k}>
            <div className="mb-1 flex justify-between text-xs"><span className="text-neutral-500">{l} <span className="text-neutral-300">({w}%)</span></span><span className="font-semibold">{r.parts[k]}%</span></div>
            <ProgressLine value={r.parts[k]} />
          </div>
        ))}
      </div>
      <div className="space-y-2 lg:text-right">
        <div className="flex flex-wrap items-center gap-2 lg:justify-end"><span className="text-xs text-neutral-500">Atual:</span><SealBadge level={r.seal?.level} testId={`company-seal-${r.id}`} /></div>
        {r.seal && <p className="text-[11px] text-neutral-400">Aprovado em {fmtDate(r.seal.approvedAt)}</p>}
        <div className="flex flex-wrap items-center gap-2 lg:justify-end"><span className="text-xs text-neutral-500">Sugerido:</span><SealBadge level={r.suggested} testId={`company-suggested-${r.id}`} /></div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {pending && <Button size="sm" className="font-semibold" onClick={() => { setSeal(r.id, { level: r.suggested, month: MONTH_KEY }); toast.success(`${SEALS[r.suggested].label} aprovado para ${r.name}.`); }} data-testid={`seal-approve-${r.id}`}><Award className="mr-1 h-4 w-4" /> Aprovar {SEALS[r.suggested].label}</Button>}
          {r.seal && <Button asChild size="sm" variant="outline" data-testid={`seal-cert-${r.id}`}><Link to={`/certificado?tipo=selo&empresa=${r.id}`}><FileBadge className="mr-1 h-4 w-4 text-gold-dark" /> Certificado</Link></Button>}
          {r.seal && <Button size="sm" variant="outline" onClick={() => { setSeal(r.id, null); toast.success("Selo revogado."); }} data-testid={`seal-revoke-${r.id}`}>Revogar</Button>}
        </div>
      </div>
    </li>
  );
}

export default function MasterRanking() {
  const { state } = useStore();
  const rows = companyRanking(state);
  return (
    <div data-testid="master-ranking-page">
      <PageHeader eyebrow={MONTH_LABEL} title="Ranking e selo de qualidade" subtitle="A pontuação é calculada automaticamente. O selo só é exibido na rede de indicações depois da sua aprovação." />
      <Panel testId="seal-criteria" className="mb-6">
        <p className="flex items-start gap-2 text-sm text-neutral-600"><Info className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> Ouro a partir de {SEALS.ouro.min} pts · Prata a partir de {SEALS.prata.min} pts · Bronze a partir de {SEALS.bronze.min} pts. Empresas com selo aprovado aparecem como recomendadas para indicações entre as empresas da consultoria.</p>
      </Panel>
      <Panel title="Ranking das empresas" testId="company-ranking">
        <ul className="divide-y divide-neutral-100">
          {rows.map((r) => <CompanyScoreCard key={r.id} r={r} referrals={state.referrals.filter((x) => x.toCompanyId === r.id).length} />)}
        </ul>
      </Panel>
    </div>
  );
}
